'use client'

import React, { useContext } from 'react'

import { Button, Popover, PopoverContent, PopoverTrigger, Separator } from '@hanzo/ui/primitives'
import { Box, cn } from '@hanzo/ui'
import { IamContext, useIam, useIamIdentity } from '@hanzo/iam/react'

import { remember } from './resume'

interface Props {
    /** Hide the sign-in button, leaving only the profile of an already signed-in
      user. Checkout wants this: it has its own sign-in step. */
  noLogin?: boolean
  className?: string
    /** Sign in in place instead of leaving for lux.id — the mobile drawer opens
      its own panel. */
  handleLogin?: () => void
}

const Widget: React.FC<Props> = ({
  noLogin = false,
  handleLogin,
  className
}) => {

  const { isAuthenticated, isLoading, login, logout } = useIam()
  const identity = useIamIdentity()

    /* Say nothing until the stored token has been read, rather than flashing
      "Login" at someone who is already signed in. */
  if (isLoading) {
    return null
  }

  if (!isAuthenticated) {
    return noLogin ? null : (
      <Button
        variant='primary'
        className={cn('h-8 w-fit !min-w-0', className)}
        onClick={handleLogin ?? (() => { remember(); void login() })}
      >
        Login
      </Button>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='icon'
          className={cn('rounded-full text-muted border-2 border-muted bg-level-1 hover:bg-level-2 hover:border-foreground hover:text-foreground uppercase w-8 h-8', className)}
        >{identity?.initials}</Button>
      </PopoverTrigger>
      <PopoverContent className='bg-level-0'>
        <Box className='grid gap-4'>
          <Box className='space-y-2 truncate'>
            <Box tag="h4" className='font-medium leading-none truncate'>{identity?.name}</Box>
            {identity?.email && identity.email !== identity.name && (
              <Box tag="p" className='text-sm opacity-50 truncate'>{identity.email}</Box>
            )}
          </Box>
          <Separator />
          <Button variant='outline' onClick={() => { logout() }}>Logout</Button>
        </Box>
      </PopoverContent>
    </Popover>
  )
}

  /* The widget rides in shared chrome, and not every site mounts <Auth> —
    app/credit carries its own root layout. A site that offers no sign-in shows
    no sign-in, which is why the context is read before any hook that needs it. */
const AuthWidget: React.FC<Props> = (props) => (
  useContext(IamContext) ? <Widget {...props} /> : null
)

export default AuthWidget
